import * as React from 'react';
import { createStyles } from '@mantine/core';
import { DatePickerInput as DatePickerInputBase, DatePickerInputProps } from '@mantine/dates';
import { IconCalendar } from '@tabler/icons-react';

// Components
import { FieldLabelTooltip } from './FieldLabelTooltip';

// Styles
import { getCommonFieldStyles } from '../helpers/fieldStyles';

// Hooks
import { useFieldTranslation } from '../hooks/useFieldTranslation';

// Types
import { GenericFieldProps } from '../types';

const useStyles = createStyles((theme) => {
    const commonFieldStyles = getCommonFieldStyles(theme);

    return {
        root: commonFieldStyles.root,
        input: commonFieldStyles.input,
        readonlyInput: commonFieldStyles.readonlyInput,
        required: commonFieldStyles.required,
        hiddenDisplay: commonFieldStyles.hiddenDisplay,
        label: commonFieldStyles.label,
        error: commonFieldStyles.error,
        wrapper: commonFieldStyles.wrapper,
        readonlyWrapper: commonFieldStyles.readonlyWrapper,
        description: commonFieldStyles.description,
        icon: commonFieldStyles.icon,
        day: {
            '&[data-weekend="true"]': {
                color: '#000'
            }
        }
    };
});

export interface CustomDatePickerInputProps extends DatePickerInputProps, GenericFieldProps {
    key?: string;
}

export const DatePickerInput: React.FC<CustomDatePickerInputProps> = ({
    readOnly,
    labelKey,
    label,
    descriptionKey,
    tooltipKey,
    parentform,
    targetperiod,
    value,
    ...props
}) => {
    const { classes, cx } = useStyles();
    const inputLabel = useFieldTranslation({
        key: labelKey,
        label,
        formData: parentform?.values,
        targetPeriod: targetperiod
    });
    const descriptionLabel = useFieldTranslation({
        key: descriptionKey,
        formData: parentform?.values,
        targetPeriod: targetperiod
    });

    const formattedValue = React.useMemo(() => (typeof value === 'string' ? new Date(value) : value), [value]);

    return (
        <DatePickerInputBase
            classNames={{
                root: classes.root,
                input: cx(classes.input, {
                    [classes.readonlyInput]: readOnly
                }),
                required: cx(classes.required, {
                    [classes.hiddenDisplay]: readOnly
                }),
                label: classes.label,
                error: classes.error,
                icon: cx(classes.icon, {
                    [classes.hiddenDisplay]: readOnly
                }),
                wrapper: cx(classes.wrapper, {
                    [classes.readonlyWrapper]: readOnly
                }),
                description: cx(classes.description, {
                    [classes.hiddenDisplay]: readOnly
                }),
                day: classes.day
            }}
            icon={<IconCalendar size="1.2rem" stroke={1.5} />}
            label={
                <>
                    <span>{inputLabel}</span>
                    {tooltipKey ? <FieldLabelTooltip translationKey={tooltipKey} /> : null}
                </>
            }
            description={descriptionLabel}
            value={formattedValue}
            popoverProps={{
                withinPortal: true
            }}
            firstDayOfWeek={0}
            {...props}
        />
    );
};
